import { useEffect, useRef } from 'react';
import { getStoredToken, TOKEN_STORAGE_KEY } from '@setvi/shared/utils/auth';

interface UseStorageTokenSyncProps {
  token: string | null;
  /** Another tab stored a different token (logged in, or switched user). */
  onTokenChanged: (token: string) => void;
  /** Another tab removed the token (logged out, or cleared storage). */
  onTokenRemoved: () => void;
}

// A user switch is `localStorage.clear()` followed by `setItem('token')`, which
// arrive as two events. Waiting a beat and reading storage once turns that into
// a single token change instead of a logout immediately followed by a login.
export const STORAGE_SYNC_DELAY_MS = 100;

/**
 * Keeps the in-memory session token in step with `localStorage` across tabs.
 * `localStorage` is shared, so a logout or login in one tab leaves every other
 * tab with a stale token in React state and no token in storage: the next
 * request goes out without one (DEV-1150).
 *
 * Storage events only reach active documents, so a tab coming back from the
 * back/forward cache or from being hidden re-reads storage as well.
 */
export const useStorageTokenSync = ({
  token,
  onTokenChanged,
  onTokenRemoved
}: UseStorageTokenSyncProps) => {
  const latest = useRef({ token, onTokenChanged, onTokenRemoved });
  useEffect(() => {
    latest.current = { token, onTokenChanged, onTokenRemoved };
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const sync = () => {
      timer = undefined;
      const { current } = latest;
      const stored = getStoredToken();

      if (stored === current.token) return;
      if (!stored) {
        if (current.token) current.onTokenRemoved();
        return;
      }
      current.onTokenChanged(stored);
    };

    const scheduleSync = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(sync, STORAGE_SYNC_DELAY_MS);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      // `key === null` is `localStorage.clear()`.
      if (event.key !== null && event.key !== TOKEN_STORAGE_KEY) return;

      scheduleSync();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleSync();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('pageshow', scheduleSync);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('pageshow', scheduleSync);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
